import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { App } from './App';
import { buildTheme } from './theme/theme';
import { useUiStore } from './stores/uiStore';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // 401s are handled by the axios interceptor; retrying them is pointless.
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

/**
 * Blob URLs are released when their query is dropped.
 *
 * A blob URL pins its data in memory until something revokes it, and file
 * previews are the one place the app makes them — a gallery of opened tiles
 * would otherwise hold every file it had shown for as long as the tab lived.
 * The cache already knows when nobody wants an entry any more, so it is the
 * right place to ask.
 */
queryClient.getQueryCache().subscribe((event) => {
  if (event.type !== 'removed') return;
  const url = (event.query.state.data as { url?: string } | undefined)?.url;
  if (typeof url === 'string' && url.startsWith('blob:')) URL.revokeObjectURL(url);
});

function Root() {
  const mode = useUiStore((s) => s.mode);
  return (
    <ThemeProvider theme={buildTheme(mode)}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
